import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
/**
 * returns function that may be called as much as desired.
 * underlying fn will be called once after debounce duration of quiescence
 * @param fn - underlying function to be called
 * @param duration - milliseconds to wait for quiescence. Ig
 * @returns function to call to eventually trigger underlying function*/
export function useDebounced<T extends (...args: any) => void>(
  fn: T,
  duration: number
): DebouncedFunction<T> {
  const [triggers, setTriggers] = useState(0);
  const [args, setArgs] = useState<Parameters<typeof fn> | undefined>(
    undefined
  );
  const eventuallyIncrementTriggers = useMemo(
    () =>
      debounce(() => {
        setTriggers((c) => c + 1);
      }, duration),
    [duration]
  );
  useEffect(() => {
    if (triggers > 0) {
      fn(...(args as any));
    }
  }, [triggers]);
  return (...args: Parameters<T>): void => {
    setArgs(args);
    eventuallyIncrementTriggers();
  };
}

interface DebouncedFunction<T extends (...args: any) => void> {
  (...args: Parameters<T>): void;
}

/**
 * returns a value that only is updated after its JSON stringified value settles for duration.
 * Useful for patterns like only triggering an API request with resolved filters after a period of inaction 
 * @param currentValue - current value. Will be returned after duration if subsequent renders do not modify
 * @param duration - milliseconds to wait
 * @returns the latest settled value
 */
export function useDebouncedValue<T>(currentValue: T, duration: number): T {
  const [value, setValue] = useState(() => currentValue);
  const debouncedSetValue = useMemo(
    () => debounce((v) => setValue(v), duration),
    [setValue, duration]
  );
  useEffect(() => {
    debouncedSetValue(currentValue);
  }, [JSON.stringify(currentValue)]);
  return value;
}
